import { FunctionComponent, HTMLAttributes } from 'react';
import cn from '../cn';

type DividerUIProps = {
  light?: boolean,
  orientation?: 'horizontal' | 'vertical',
  variant?: 'fullWidth' | 'middle' | 'inset',
  flexItem?: boolean,
} & HTMLAttributes<HTMLHRElement>;

/**
 * MUI Divider. Renders `<hr>`; the base resets UA `<hr>` styling, which
 * `preflight: false` leaves in place.
 *
 * The base lives in `.ds-hr` (design-system.css) and the variants ride on
 * `data-ds-*` attributes. It used to be an inline `style` object, which meant
 * call-site classNames were silently discarded - inline styles outrank every
 * stylesheet rule, so a `<Divider className="mt-4 border-b-2" />` rendered with
 * no margin at 1px, and 13 of the 21 call sites in the app were passing margins
 * that never applied. A component whose base is inline cannot be restyled by
 * its callers; keep it in CSS so `important: '#root'` utilities win.
 *
 * Colour comes from `--ds-border` rather than a hardcoded `rgba(0,0,0,…)`, so
 * dividers follow the theme instead of vanishing in dark mode.
 */
const DividerUI: FunctionComponent<DividerUIProps> = ({
  light = false,
  orientation = 'horizontal',
  variant = 'fullWidth',
  flexItem = false,
  className = '',
  style,
  ...rest
}) => (
  <hr
    className={cn('ds-hr', className)}
    data-ds-light={light ? 'true' : undefined}
    data-ds-orientation={orientation === 'vertical' ? 'vertical' : undefined}
    data-ds-variant={variant === 'fullWidth' ? undefined : variant}
    data-ds-flex-item={flexItem ? 'true' : undefined}
    style={style}
    {...rest}
  />
);

export default DividerUI;
